<template>
  <CardSection :loading="isSavableStateLoading" :error="majorError">
    <template #header>
      Manage Match Tag Categories
    </template>

    <template #body>
      <div class="form-group row">
        <div class="form-group mr-2 mb-0 col-12 d-flex">
          <router-link :to="{ name: 'admin_tag_categories_new' }" class="btn btn-primary btn-sm ml-auto">
            Add New Tag Category
          </router-link>
        </div>
      </div>
      <PortalTable ref="tag_categories_index" table-id="tag_categories_index"
        :table-data="records"
        :total-records="totalRecords"
        :page="pageNumber"
        :page-count="pageCount"
        :loading="isSavableStateProcessing"
        :filter-values="filterValues"
        filter-display="row"
        empty="No Tag Categories found!"
        @on-sort="handleSortChange"
        @on-page="handlePageChange"
        @on-filter-change="onFilterChange">
        <template #columns>
          <Column field="category_name" 
            header="Category Name" 
            filter-field="category_ids" 
            sortable
            :show-filter-menu="false">
            <template #filter="{ filterModel = { value: null }, filterCallback }">
              <MultiSelectFilter ref="category_ids" 
                filter-field="category_ids"
                option-value="id"
                option-label="code"
                :options="tagStore.getCategories"
                @change="onChange" />
            </template>
          </Column>
          <Column field="category_identifier" 
            sortable
            header="Category Identifier"/>
          <Column field="created_by.full_name" 
            header="Created By" />
          <Column field="updated_by.full_name" 
            header="Updated By" />
          <Column field="disabled_at" 
            header="State"
            filter-field="state"
            :show-filter-menu="false"
            :filter-menu-style="{ width: '14rem' }"
            style="min-width: 14rem">
            <template #filter>
              <MultiSelectFilter ref="state" 
                filter-field="state"
                option-value="code"
                cache-context-key="state"
                class="form-control form-control-select"
                option-label="name"
                :options="stateOptions"
                @change="onChange" />
            </template>
            <template #body="slotProps">
              {{ slotProps.data.disabled_at ? 'Disabled' : 'Enabled' }}
            </template>
          </Column>
          <Column field="internal_reporting_label" header="Internal Reporting" />
          <Column field="external_reporting_label" header="External Reporting" />
          <Column header="Actions" class="btn-2-icon-group">
            <template #body="slotProps">
              <div class="d-flex">
                <router-link :to="{ name: 'admin_tag_categories_edit', params: { id: slotProps.data.id, createNew: 'true' } }"
                  class="action btn btn-fill btn-outline-primary mx-2">
                  Edit Category
                </router-link>
                <router-link :to="{ name: 'admin_tag_categories_edit_tags', params: { id: slotProps.data.id, createNew: 'true' } }"
                  class="action btn btn-fill btn-outline-primary mx-2">
                  Edit Tags
                </router-link>
              </div>
            </template>
          </Column>
        </template>
      </PortalTable>
    </template>
  </CardSection>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
// @ts-ignore
import CardSection from "@/src/components/shared/CardSection.vue";
// @ts-ignore
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import { FilterMatchMode } from '@primevue/core/api';
// @ts-ignore
import MultiSelectFilter from "@/src/components/shared/FormComponents/MultiSelectFilter.vue";
// @ts-ignore
import { useTagStore } from '@/src/stores/tag';
import { TagCategory } from "@/src/models/match/tag-category";
import { useSavableState } from '@/src/composables/useSavableState';

const tagStore = useTagStore();

const {
  majorError,
  isSavableStateLoading,
  isSavableStateProcessing,
  setSavableStateLoading,
  setSavableStateProcessing,
  setSavableStateIdle,
  handleMajorError,
} = useSavableState();

// paginationMixin
const records = ref<any[]>([]);
const totalRecords = ref<number>(0);
const pageNumber = ref<number>(1);
const pageCount = ref<number | null>(null);
const currentSortField = ref<string>('');
const currentSortType = ref<string | null>('');
const searchParams = ref<Record<string, any>>({});

// Template refs
const tag_categories_index = ref<any>(null);

// Component state
const filterValues = ref({
  "category_name": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  "state": { value: ['enabled'], matchMode: FilterMatchMode.IN },
  "category_ids": { value: null, matchMode: FilterMatchMode.STARTS_WITH }
});

const stateOptions = [
  { code: "enabled", name: "Enabled" },
  { code: "disabled", name: "Disabled" }
];

function processPagyResponse(response: any, processDataFn: ((data: any[]) => any[]) | null = null) {
  records.value = processDataFn ? processDataFn(response.data) : response.data;
  if (response.pagy) {
    totalRecords.value = response.pagy.count;
    pageCount.value = response.pagy.last;
    pageNumber.value = response.pagy.page;
  }
}

function processIncomingData(data: any[]): TagCategory[] {
  return data.map((r: any) => TagCategory.buildFromAPI(r));
}

async function getRowData(reset = false) {
  setSavableStateProcessing();
  if (reset) pageNumber.value = 1;

  const params = {
    ...searchParams.value,
    page: pageNumber.value,
    sort_by: currentSortField.value,
    sort_type: currentSortType.value,
  };

  try {
    const response = await tagStore.loadPaginatedTagCategories(params);
    processPagyResponse(response, processIncomingData);
    setSavableStateIdle();
  } catch(error) {
    handleMajorError(error);
  }
}

function handlePageChange(page: number) {
  pageNumber.value = page;
  getRowData();
}

function handleSortChange(params: any) {
  currentSortField.value = params.sortField;
  switch(params.sortOrder) {
    case 1:
      currentSortType.value = 'asc';
      break;
    case -1:
      currentSortType.value = 'desc';
      break;
    default:
      currentSortType.value = null;
  }
  getRowData();
}

function onFilterChange(event: any) {
  searchParams.value = event;
  getRowData(true);
}

function onChange(event: any) {
  tag_categories_index.value?.resetFilters(event);
}

onMounted(async () => {
  try {
    setSavableStateLoading();
    await tagStore.loadAllTagCategories();

    window.localStorage.setItem('multiselectfilter.state', 'enabled');
    await getRowData();
    setSavableStateIdle();
  } catch(error) {
    handleMajorError(error);
  }
});
</script>
